import { memo, useCallback, useMemo, useState } from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { Box, Stack, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { useParams } from 'react-router-dom';
import ColumnVisibilityMenu from 'src/components/ColumnVisibilityMenu';
import Iconify from 'src/components/Iconify';
import ListActionBar from 'src/components/ListActionBar';
import LoadingRows from 'src/components/Table/LoadingRows';
import NoDataCell from 'src/components/NoDataCell';
import TableContainer from 'src/components/Table/TableContainer';
import TableHeadTypography from 'src/components/TableHeadTypography';
import { TableCellTypography } from 'src/components/GenericTableCell';
import { EXPANDED_ROWS_LIMIT } from 'src/constants/table';
import { useColumnVisibility } from 'src/hooks/columnVisibility';
import useTypographyMediaQuery from 'src/hooks/useTypographyMediaQuery';
import { useInfiniteDeviceUnmatchedFiles } from 'src/hooks/device';
import { formatDate } from 'src/utils/formatTime';
import HighlightedText from 'src/components/HighlightedText';
import DeviceUnmatchedFileDetailsModal from './DeviceUnmatchedFileDetailsModal';
import {
  DEFAULT_VISIBLE_DEVICE_UNMATCHED_FILES_COLUMNS,
  DEVICE_UNMATCHED_FILES_COLUMNS,
} from './constants';

const ELLIPSIS_SX = {
  m: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  display: 'block',
};

const HASH_SX = { ...ELLIPSIS_SX, color: '#9CA3AF', fontSize: '0.68rem', lineHeight: 1.3 };

const DeviceUnmatchedColumnHeader = ({
  column,
  sort,
  onSort,
  mediumScreen,
  largeScreen,
  smallScreen,
}) => {
  const sortActive = column.sortable && sort.column === column.sortKey;

  return (
    <Stack
      direction="row"
      gap={1}
      alignItems="center"
      onClick={column.sortable ? () => onSort(column) : undefined}
      sx={{ cursor: column.sortable ? 'pointer' : 'default' }}>
      <TableHeadTypography
        hasSorting={sortActive}
        mediumScreen={mediumScreen}
        largeScreen={largeScreen}
        smallScreen={smallScreen}>
        {column.label}
      </TableHeadTypography>
      {column.sortable && (
        <Iconify
          icon={
            sortActive
              ? sort.direction === 'asc'
                ? 'mdi:arrow-up'
                : 'mdi:arrow-down'
              : 'mdi:swap-vertical'
          }
          width={14}
          height={14}
          color={sortActive ? '#7DFF92' : '#9CA3AF'}
        />
      )}
    </Stack>
  );
};

const UnmatchedFilesTable = () => {
  const { deviceId } = useParams();

  const { mediumScreen, largeScreen, smallScreen } = useTypographyMediaQuery();
  const [searchValue, setSearchValue] = useState('');
  const [sort, setSort] = useState({ column: 'last30Count', direction: 'desc' });
  const [detailsModal, setDetailsModal] = useState({ open: false, row: null });

  const apiQuery = useMemo(() => {
    const params = {};
    const trimmedSearch = typeof searchValue === 'string' ? searchValue.trim() : '';

    if (trimmedSearch) {
      params.Search = trimmedSearch;
    }

    if (sort.column) {
      params.SortColumn = sort.column;
      params.SortDesc = sort.direction === 'desc';
    }

    return params;
  }, [searchValue, sort]);

  const unmatchedFilesQuery = useInfiniteDeviceUnmatchedFiles({
    deviceId,
    apiQuery,
    limit: EXPANDED_ROWS_LIMIT,
    enabled: true,
  });

  const results = useMemo(
    () => unmatchedFilesQuery.data?.pages?.flatMap(page => page.results || []) || [],
    [unmatchedFilesQuery.data],
  );
  const loadMore = useCallback(() => {
    if (unmatchedFilesQuery.hasNextPage) {
      unmatchedFilesQuery.fetchNextPage();
    }
  }, [unmatchedFilesQuery]);

  const [sentryRef] = useInfiniteScroll({
    loading: unmatchedFilesQuery.isFetchingNextPage,
    hasNextPage: unmatchedFilesQuery.hasNextPage,
    disabled: false,
    onLoadMore: loadMore,
    rootMargin: '0px 0px 400px 0px',
  });

  const handleSort = useCallback(
    column => {
      if (!column.sortable) {
        return;
      }

      if (sort.column === column.sortKey) {
        setSort({
          column: column.sortKey,
          direction: sort.direction === 'asc' ? 'desc' : 'asc',
        });
        return;
      }

      setSort({ column: column.sortKey, direction: 'desc' });
    },
    [sort],
  );

  const {
    visibleColumnDefinitions: visibleColumns,
    isColumnVisible,
    setColumnVisible,
    resetColumns,
  } = useColumnVisibility(
    'deviceUnmatchedFiles',
    DEVICE_UNMATCHED_FILES_COLUMNS,
    DEFAULT_VISIBLE_DEVICE_UNMATCHED_FILES_COLUMNS,
  );

  const totalWeight = visibleColumns.reduce((sum, column) => sum + column.weight, 0);
  const trimmedSearch = searchValue.trim();

  const renderCell = (column, row) => {
    switch (column.key) {
      case 'fileName':
        return (
          <TableCell key={column.key} sx={{ py: 0.5, minWidth: 0 }}>
            <Stack spacing={0.25} sx={{ minWidth: 0 }}>
              <TableCellTypography sx={ELLIPSIS_SX}>
                <HighlightedText text={row.fileName} searchValue={trimmedSearch} fallback="--" />
              </TableCellTypography>
              <TableCellTypography component="div" sx={HASH_SX}>
                <HighlightedText text={row.sha256} searchValue={trimmedSearch} fallback="--" />
              </TableCellTypography>
            </Stack>
          </TableCell>
        );
      case 'authenticodeHash':
        return (
          <TableCell key={column.key} sx={{ py: 0.5, minWidth: 0 }}>
            <TableCellTypography
              component="div"
              title={row.authenticodeHashSha256 || undefined}
              sx={HASH_SX}>
              <HighlightedText
                text={row.authenticodeHashSha256}
                searchValue={trimmedSearch}
                fallback="--"
              />
            </TableCellTypography>
          </TableCell>
        );
      case 'lastSeen':
        return (
          <TableCell key={column.key}>
            {row.lastSeenDate ? formatDate(row.lastSeenDate, 'yyyy-MM-dd') : '--'}
          </TableCell>
        );
      default:
        return <TableCell key={column.key}>{row[column.key] ?? 0}</TableCell>;
    }
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <ListActionBar
        onSearch={setSearchValue}
        defaultSearch={searchValue}
        searchPlaceholder="File name, SHA-256 or Authenticode hash"
        actionArea={
          <ColumnVisibilityMenu
            columns={DEVICE_UNMATCHED_FILES_COLUMNS}
            isVisible={isColumnVisible}
            onChange={setColumnVisible}
            onReset={resetColumns}
          />
        }
      />

      <TableContainer roundedTop styles={{ maxHeight: 'calc(100vh - 354px)' }}>
        <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
          <colgroup>
            {visibleColumns.map(column => (
              <col key={column.key} style={{ width: `${(column.weight / totalWeight) * 100}%` }} />
            ))}
          </colgroup>
          <TableHead sx={{ backgroundColor: '#3f3f3f' }}>
            <TableRow>
              {visibleColumns.map(column => (
                <TableCell key={column.key} sx={{ p: '8px 16px', verticalAlign: 'middle' }}>
                  <DeviceUnmatchedColumnHeader
                    column={column}
                    sort={sort}
                    onSort={handleSort}
                    mediumScreen={mediumScreen}
                    largeScreen={largeScreen}
                    smallScreen={smallScreen}
                  />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody sx={{ backgroundColor: '#4B4B4B' }}>
            {unmatchedFilesQuery.isLoading ? (
              <LoadingRows columnCount={visibleColumns.length} rowCount={4} />
            ) : (
              <>
                {results.map((row, index) => (
                  <TableRow
                    key={row.fileId || row.sha256 || index}
                    ref={index === results.length - 1 ? sentryRef : null}
                    onClick={() => setDetailsModal({ open: true, row })}
                    sx={{ cursor: 'pointer' }}>
                    {visibleColumns.map(column => renderCell(column, row))}
                  </TableRow>
                ))}
                {!unmatchedFilesQuery.isLoading && unmatchedFilesQuery.isFetchingNextPage && (
                  <LoadingRows columnCount={visibleColumns.length} rowCount={3} />
                )}
                {!unmatchedFilesQuery.isLoading &&
                  !unmatchedFilesQuery.isFetchingNextPage &&
                  !results.length && (
                    <NoDataCell colSpan={visibleColumns.length}>
                      No unmatched files found.
                    </NoDataCell>
                  )}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <DeviceUnmatchedFileDetailsModal
        open={detailsModal.open}
        onClose={() => setDetailsModal({ open: false, row: null })}
        deviceId={deviceId}
        fileId={detailsModal.row?.fileId}
        fallbackRow={detailsModal.row}
      />
    </Box>
  );
};

export default memo(UnmatchedFilesTable);
